import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import type { ComponentProps } from "react";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";

/** Holds the selected `value`; wraps a TabList and one TabPanel per tab. */
export function Tabs(props: ComponentProps<typeof BaseTabs.Root>) {
  return <BaseTabs.Root {...props} />;
}

/** The row of tabs, with the underline that glides to the active one. */
export function TabList({
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof BaseTabs.List>, "className"> & { className?: string }) {
  return (
    <BaseTabs.List
      className={cn("relative flex items-end gap-6 border-b border-border-subtle", className)}
      {...props}
    >
      {children}
      {/* Slides by transform only (no layout per frame); the width snaps to the new tab. */}
      <BaseTabs.Indicator className="absolute bottom-0 left-0 h-0.5 w-(--active-tab-width) translate-x-(--active-tab-left) bg-accent-default transition-[translate] duration-base ease-standard motion-reduce:transition-none" />
    </BaseTabs.List>
  );
}

/** One tab: its label is `children`, its `value` matches a TabPanel. */
export function TabItem({
  className,
  ...props
}: Omit<ComponentProps<typeof BaseTabs.Tab>, "className"> & { className?: string }) {
  return (
    <BaseTabs.Tab
      className={cn(
        "cursor-pointer rounded-xs pb-3 font-body text-body-b1-strong text-text-tertiary hover:text-text-secondary data-active:text-text-primary",
        FOCUS_RING,
        className,
      )}
      {...props}
    />
  );
}

/** The content for one tab. */
export function TabPanel(props: ComponentProps<typeof BaseTabs.Panel>) {
  return <BaseTabs.Panel {...props} />;
}
